import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosConfig from "../../axiosConfig";
import { RootState } from "../../app/store";

export type SettingDeliveryCost = {
  id: number;
  deviation: number;
  priceRatio: number;
  maxCapacity: number;
  maxCapacityPerCustomer: number;
  isDelete: boolean;
  actions: string;
};

export type SystemSetting = {
  dataDeliveryDate: Array<SettingDeliveryCost>;
  referenceDeliveryDays: number;
  limitAIEstimate: number;
  limitBackloggedTickets: number;
  limitCapacityOverload: number;
  limitCapacityNumber: number;
  limitDeadlineDelivery: number;
  maxProcessingCapacity: number;
  failedEstimateDisplayDays: number;
  failedEstimateNotifyTiming: string;
  commentNotifyFrequency: string;
  customerApprovalEnabled: boolean;
  workingTime: {
    start: string;
    end: string;
    timeAllocation: number;
  };
};

const initialState: SystemSetting = {
  dataDeliveryDate: [],
  referenceDeliveryDays: 0,
  limitAIEstimate: 0,
  limitBackloggedTickets: 0,
  limitCapacityOverload: 0,
  limitCapacityNumber: 0,
  limitDeadlineDelivery: 0,
  maxProcessingCapacity: 0,
  failedEstimateDisplayDays: 0,
  failedEstimateNotifyTiming: "order",
  commentNotifyFrequency: "immediate",
  customerApprovalEnabled: false,
  workingTime: {
    start: "09:00",
    end: "18:00",
    timeAllocation: 0,
  },
};

export const fetchSystemSettingAsync = createAsyncThunk(
  "systemSetting/fetchSystemSetting",
  async () => {
    const response = await axiosConfig({
      method: "get",
      url: "/settings/all",
    });
    return response.data.body;
  }
);

export const systemSettingSlice = createSlice({
  name: "systemSetting",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSystemSettingAsync.fulfilled, (state, action: any) => {
      const data = action.payload;
      state.dataDeliveryDate = [];
      data.flexible_calculation_settings.forEach((item: any) => {
        state.dataDeliveryDate.push({
          id: item.id,
          deviation: item.deviation,
          priceRatio: item.price_ratio,
          maxCapacity: item.max_capacity ?? 0,
          maxCapacityPerCustomer: item.max_capacity_per_customer ?? 0,
          isDelete: false,
          actions: "",
        });
      });
      state.referenceDeliveryDays = data.settings.reference_days_delivery;
      state.limitAIEstimate = data.settings.ai_estimate_threshold;
      state.limitBackloggedTickets = data.settings.unprocessed_ticket_hours;
      state.limitCapacityOverload = data.settings.sub_capacity_ratio;
      state.limitCapacityNumber = data.settings.sub_standard_capacity;
      state.limitDeadlineDelivery = data.settings.sub_deadline_ratio;
      state.maxProcessingCapacity = data.settings.max_processing_capacity ?? 0;
      state.failedEstimateDisplayDays =
        data.settings.failed_estimate_display_days ?? 0;
      state.failedEstimateNotifyTiming =
        data.settings.failed_estimate_notify_timing ?? "order";
      state.commentNotifyFrequency =
        data.settings.comment_notify_frequency ?? "immediate";
      state.customerApprovalEnabled =
        data.settings.customer_approval_enabled ?? false;
      state.workingTime.start = data.settings.working_start_time;
      state.workingTime.end = data.settings.working_end_time;
      state.workingTime.timeAllocation = data.settings.sub_allocation_ratio;
    });
  },
});

// export const {} = systemSettingSlice.actions;

export const getSystemSettingState = (state: RootState) => state.systemSetting;
export const getDataDeliveryDateSetting = (state: RootState) =>
  [...state.systemSetting.dataDeliveryDate].sort(
    (a, b) => a.deviation - b.deviation
  );
export const getReferenceDeliveryDaySetting = (state: RootState) =>
  state.systemSetting.referenceDeliveryDays;

export default systemSettingSlice.reducer;
